/** CPT display order — matches chart legend in design (2.png) */
export const CPT_CODES_ORDER = ["98975", "98985", "98977", "98980", "98979", "98981"];

export const CPT_COLOR_MAP = {
  98975: "#4582F2",
  98985: "#B920B5",
  98977: "#00B7A6",
  98979: "#FF463A",
  98980: "#915EF2",
  98981: "#F59F2F",
};

export const CPT_CHART_LABELS = CPT_CODES_ORDER.map((code) => `CPT ${code}`);

export const getCptColor = (code) => CPT_COLOR_MAP[code] ?? "#9e9e9e";

const normalizeMonthlyItem = (item) => {
  if (!item || typeof item !== "object") return null;

  const monthLabel =
    item.monthLabel ?? item.month ?? item.label ?? item.monthName ?? "";

  const countsSource =
    item.cptCounts ?? item.claimsByCpt ?? item.breakdown ?? item.cptBreakdown ?? item;

  const counts = CPT_CODES_ORDER.reduce((acc, code) => {
    acc[code] = Number(countsSource[code] ?? countsSource[`cpt${code}`] ?? 0) || 0;
    return acc;
  }, {});

  return { monthLabel, counts };
};

export const parseMonthlyTrend = (data) => {
  const raw =
    data?.monthlyTrend ??
    data?.monthlyBreakdown ??
    data?.cptMonthlyTrend ??
    data?.monthlyClaims ??
    [];

  if (!Array.isArray(raw) || !raw.length) return [];

  return raw.map(normalizeMonthlyItem).filter((item) => item?.monthLabel);
};

export const RTM_CLAIM_DETAILS_PAGE_SIZE = 20;

export const RTM_CLAIM_DATE_FIELDS = new Set([
  "dateOfServiceFrom",
  "dateOfServiceTo",
  "careEpisodeStartDate",
  "careEpisodeEndDate",
]);

export const RTM_CLAIM_ELIGIBLE_COLUMNS = [
  { key: "customerName", header: "Customer Name" },
  { key: "clinicName", header: "Clinic Name" },
  { key: "therapist", header: "Therapist" },
  { key: "patientName", header: "Patient Name" },
  { key: "mrn", header: "MRN" },
  { key: "condition", header: "Condition" },
  { key: "dateOfServiceFrom", header: "Date(s) of service From" },
  { key: "dateOfServiceTo", header: "Date(s) of service To" },
  { key: "cpt", header: "CPT" },
  { key: "modifier", header: "Modifier" },
  { key: "subunits", header: "Subunits" },
  { key: "units", header: "Units" },
  { key: "renderingProviderNpi", header: "Rendering provider NPI" },
  { key: "renderingProviderEmail", header: "Rendering provider Email" },
];

export const RTM_CLAIM_INELIGIBLE_COLUMNS = [
  { key: "customerName", header: "Customer Name" },
  { key: "clinicName", header: "Clinic Name" },
  { key: "patientName", header: "Patient Name" },
  { key: "mrn", header: "MRN" },
  { key: "condition", header: "Condition" },
  { key: "careEpisodeStartDate", header: "Care episode Start Date" },
  { key: "careEpisodeEndDate", header: "Care episode End Date" },
  { key: "dateOfServiceFrom", header: "Date(s) of service From" },
  { key: "dateOfServiceTo", header: "Date(s) of service To" },
  { key: "cpt", header: "CPT" },
  { key: "modifier", header: "Modifier" },
  { key: "subunits", header: "Subunits" },
  { key: "units", header: "Units" },
  { key: "transmissionDays", header: "Transmission Days" },
];

export const parseRtmClaimsResponse = (response) => {
  const data = response?.data?.data ?? response?.data ?? {};
  const cptCodes = Array.isArray(data.cptCodes) ? data.cptCodes : [];

  return {
    totalClaimsCount: data.totalClaimsCount ?? null,
    cptCodes,
    monthlyTrend: parseMonthlyTrend(data),
    appliedFilters: data.appliedFilters ?? null,
  };
};

export const parseRtmClaimDetailsPage = (response) => {
  const data = response?.data?.data ?? response?.data ?? {};
  const records = data.claimRecords ?? {};
  const list = Array.isArray(records.pageContent) ? records.pageContent : [];
  const pageNo = records.pageNo ?? 0;
  const totalPage = records.totalPage ?? 1;

  return {
    list,
    pageNo,
    pageSize: records.pageSize ?? RTM_CLAIM_DETAILS_PAGE_SIZE,
    totalPage,
    totalRecords: records.totalRecords ?? list.length,
    hasMore: pageNo + 1 < totalPage,
    eligibleOnly: data.eligibleOnly ?? true,
    appliedFilters: data.appliedFilters ?? null,
  };
};

const escapeCsvCell = (value) => {
  if (value == null || value === "") return "";
  const str = String(value);
  if (str.includes(",") || str.includes('"') || str.includes("\n")) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

export const formatRtmClaimCsvCell = (key, value, formatDate) => {
  if (RTM_CLAIM_DATE_FIELDS.has(key)) {
    const formatted = formatDate(value);
    if (formatted) return formatted;
    if (value === "-" || value === "—" || value === "--") return "--";
    return "";
  }
  return escapeCsvCell(value);
};

export const convertRtmClaimDetailsToCsv = (rows, eligibleOnly, formatDate) => {
  const columns = eligibleOnly ? RTM_CLAIM_ELIGIBLE_COLUMNS : RTM_CLAIM_INELIGIBLE_COLUMNS;
  const safeRows = Array.isArray(rows) ? rows : [];

  return [
    columns.map((col) => col.header).join(","),
    ...safeRows.map((row) => columns
      .map((col) => formatRtmClaimCsvCell(col.key, row[col.key], formatDate))
      .join(",")),
  ].join("\n");
};

export const downloadRtmClaimDetailsCsv = (rows, eligibleOnly, formatDate) => {
  const fileName = eligibleOnly
    ? "RTM_Claims_Eligible_Report"
    : "RTM_Claims_Ineligible_Report";
  const csvData = convertRtmClaimDetailsToCsv(rows, eligibleOnly, formatDate);
  const blob = new Blob([csvData], { type: "text/csv" });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", `${fileName}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export const sortCptCodes = (cptCodes) => {
  const orderIndex = CPT_CODES_ORDER.reduce((acc, code, index) => {
    acc[code] = index;
    return acc;
  }, {});

  return [...cptCodes].sort(
    (a, b) => (orderIndex[a.code] ?? 99) - (orderIndex[b.code] ?? 99)
  );
};

export const computeChartYAxisMax = (monthlyTrend) => {
  let maxTotal = 0;
  monthlyTrend.forEach(({ counts }) => {
    const total = CPT_CODES_ORDER.reduce((sum, code) => sum + (counts[code] ?? 0), 0);
    if (total > maxTotal) maxTotal = total;
  });

  if (maxTotal <= 0) return 800;
  return Math.ceil(maxTotal / 200) * 200;
};

/** Round up the highest CPT claims count for a clean y-axis max */
export const computeCptCountYAxisMax = (counts = []) => {
  const max = Math.max(0, ...counts.map((n) => Number(n) || 0));
  if (max <= 0) return 100;
  if (max <= 50) return Math.ceil(max / 10) * 10;
  if (max <= 500) return Math.ceil(max / 50) * 50;
  if (max <= 2000) return Math.ceil(max / 100) * 100;
  return Math.ceil(max / 200) * 200;
};

export const buildCptBarChartData = (cptCodes = []) => {
  const countByCode = (cptCodes || []).reduce((acc, item) => {
    if (item?.code) acc[item.code] = Number(item.claimsCount) || 0;
    return acc;
  }, {});

  return CPT_CODES_ORDER.map((code) => ({
    code,
    count: countByCode[code] ?? 0,
    color: CPT_COLOR_MAP[code],
  }));
};

const MONTH_LABELS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

export const RTM_CLAIMS_EARLIEST_MONTH = { year: 2018, month: 11 };

/** Month options for RTM Claims filter — previous month back through Dec 2018 */
export const buildRtmClaimsMonthOptions = (now = new Date()) => {
  const options = [];
  let year = now.getFullYear();
  let month = now.getMonth() - 1;

  if (month < 0) {
    month = 11;
    year -= 1;
  }

  const { year: endYear, month: endMonth } = RTM_CLAIMS_EARLIEST_MONTH;

  while (year > endYear || (year === endYear && month >= endMonth)) {
    const id = `${year}-${String(month + 1).padStart(2, "0")}`;
    options.push({ id, label: `${MONTH_LABELS[month]} ${year}` });

    month -= 1;
    if (month < 0) {
      month = 11;
      year -= 1;
    }
  }

  return options;
};

export const getRtmClaimsMonthLabel = (monthValue, options = buildRtmClaimsMonthOptions()) => {
  if (!monthValue) return "Select month";
  const match = options.find((opt) => opt.id === monthValue);
  return match?.label ?? monthValue;
};
